import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Bot, Target, Clock } from "lucide-react";
import HexagonSkills from "../../../components/HexagonSkills";
import type {
  SkillsMap,
  AIRecommendation,
} from "../../../hooks/candidate/useSkillsAssessment";
import { ROLE_SKILL_SETS } from "../../../utils/skillDataConfig";

interface SkillsTabProps {
  candidateName: string;
  skillsLoading: boolean;
  skillsAssessmentType: "personal" | "technical";
  setSkillsAssessmentType: (type: "personal" | "technical") => void;
  selectedRole: string;
  setSelectedRole: (role: string) => void;
  roleSkillSets: typeof ROLE_SKILL_SETS;
  handleGenerateTechnicalSkills: () => Promise<void>;
  isGeneratingTechnicalSkills: boolean;
  technicalSkills: SkillsMap | null;
  candidateSkills: SkillsMap | null;
  skillsVersion: number;
  updateSkills: (skills: SkillsMap) => void;
  aiRecommendations: AIRecommendation[] | null;
  isGeneratingRecommendations: boolean;
  handleGenerateAIRecommendations: () => Promise<void>;
}

const SkillsTab: React.FC<SkillsTabProps> = ({
  candidateName,
  skillsLoading,
  skillsAssessmentType,
  setSkillsAssessmentType,
  selectedRole,
  setSelectedRole,
  roleSkillSets,
  handleGenerateTechnicalSkills,
  isGeneratingTechnicalSkills,
  technicalSkills,
  candidateSkills,
  skillsVersion,
  updateSkills,
  aiRecommendations,
  isGeneratingRecommendations,
  handleGenerateAIRecommendations,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);

  if (skillsLoading) {
    return (
      <div className="skills-tab">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("common:loading.skillsAssessment")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skills-tab">
      <div className="skills-header">
        <h3>{t("candidates.detail.skillsAssessment")}</h3>
        <div
          className="assessment-type-selector"
          style={{ marginBottom: "20px" }}
        >
          <label
            htmlFor="assessment-type-select"
            style={{ marginRight: "10px", fontWeight: "bold" }}
          >
            {t("candidates.detail.assessmentType")}:
          </label>
          <select
            id="assessment-type-select"
            value={skillsAssessmentType}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setSkillsAssessmentType(
                e.target.value as "personal" | "technical",
              )
            }
            className="role-select"
            style={{ padding: "8px", fontSize: "14px" }}
          >
            <option value="personal">
              {t("candidates.detail.personalSkills")}
            </option>
            <option value="technical">
              {t("candidates.detail.technicalSkills")}
            </option>
          </select>
        </div>

        {skillsAssessmentType === "personal" && (
          <div className="role-selector">
            <label htmlFor="role-template-select">
              {t("candidates.detail.roleTemplate")}:
            </label>
            <select
              id="role-template-select"
              value={selectedRole}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setSelectedRole(e.target.value)
              }
              className="role-select"
            >
              {Object.entries(roleSkillSets).map(([key, role]) => (
                <option key={key} value={key}>
                  {(role as { name: string }).name}
                </option>
              ))}
            </select>
          </div>
        )}

        {skillsAssessmentType === "technical" && (
          <div
            className="technical-skills-selector"
            style={{ marginBottom: "20px" }}
          >
            <button
              onClick={handleGenerateTechnicalSkills}
              disabled={isGeneratingTechnicalSkills}
              className="btn-primary"
              style={{ marginTop: "10px" }}
            >
              <Bot size={16} />{" "}
              {isGeneratingTechnicalSkills
                ? t("common:loading.generating")
                : t("candidates.detail.generateTechnicalSkills")}
            </button>
            <p style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
              {t("candidates.detail.generateTechnicalSkillsDescription")}
            </p>
          </div>
        )}
      </div>

      {skillsAssessmentType === "personal" ? (
        <>
          <HexagonSkills
            key={`skills-${skillsVersion}`}
            skills={
              candidateSkills ||
              (roleSkillSets[selectedRole] as { skills: SkillsMap }).skills
            }
            title={t("candidates.detail.personalSkillsTitle", {
              name: candidateName,
            })}
            editable={true}
            onSkillsChange={(newSkills: SkillsMap) => updateSkills(newSkills)}
            size="large"
          />

          <div className="skill-recommendations">
            <div className="recommendations-header">
              <h4>{t("candidates.detail.aiSkillRecommendations")}</h4>
              <button
                onClick={handleGenerateAIRecommendations}
                disabled={isGeneratingRecommendations}
                className="btn btn-outline"
              >
                <Bot size={16} />{" "}
                {isGeneratingRecommendations
                  ? t("common:loading.generating")
                  : t("candidates.detail.generateAIRecommendations")}
              </button>
            </div>

            {aiRecommendations ? (
              <div className="recommendations-list">
                {aiRecommendations.slice(0, 5).map((rec) => (
                  <div
                    key={rec.skill}
                    className={`recommendation-item ${rec.priority}`}
                  >
                    <div className="recommendation-header">
                      <span className="skill-name">{rec.skill}</span>
                      <span className="priority-badge">{rec.priority}</span>
                    </div>
                    <div className="recommendation-details">
                      <span className="current-level">{rec.current}%</span>
                      <span className="arrow">→</span>
                      <span className="target-level">{rec.target}%</span>
                    </div>
                    <div className="recommendation-description">
                      {rec.reason}
                    </div>
                    <div className="recommendation-action">
                      {rec.actionPlan}
                    </div>
                    <div className="recommendation-impact">
                      <Target size={14} /> {t("candidates.detail.impact")}:{" "}
                      {rec.impactOnRole}
                    </div>
                    <div className="recommendation-timeframe">
                      <Clock size={14} /> {rec.timeframe}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-recommendations">
                <p>{t("candidates.detail.noRecommendationsYet")}</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {technicalSkills ? (
            <HexagonSkills
              skills={technicalSkills}
              title={t("candidates.detail.technicalSkillsTitle", {
                name: candidateName,
              })}
              editable={false}
              size="large"
            />
          ) : (
            <div
              className="no-technical-skills"
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              <p>{t("candidates.detail.noTechnicalSkillsYet")}</p>
              <p style={{ marginTop: "10px", fontSize: "14px" }}>
                {t("candidates.detail.technicalSkillsAnalysisDescription")}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SkillsTab;
